/**
 * Media Plan API Client
 *
 * Service layer for the self-serve media plan flow.
 * In mock mode (VITE_ENABLE_MOCK_GENERATION=true), returns mock data with
 * simulated network delays so loading states are visible during development.
 *
 * Entity model: BusinessProfile → Lead → MediaPlan → Campaign → Creative
 * See docs/entity-relationship.md for the full ER diagram.
 *
 * When real APIs are ready, swap the mock implementations in each method
 * for actual fetch calls -- nothing else changes.
 */

import {
  MOCK_BUSINESS_PROFILE,
  MOCK_PLANS,
  MOCK_DIFFERENTIATORS,
  MOCK_VOICE_OPTIONS,
  MOCK_CHARACTER_OPTIONS,
  MOCK_CAMPAIGN,
  MOCK_CREATIVES,
  MOCK_GENERATION_STEPS,
  MOCK_REPORT,
  MOCK_DASHBOARD_CAMPAIGNS,
  isMockMode,
} from './mockMediaPlanData';

// ── Base URL ────────────────────────────────────────────────────────────────

const API_BASE_URL = import.meta.env.VITE_MEDIA_PLAN_API_URL || '/api/v1';

// ── Error class ─────────────────────────────────────────────────────────────

export class MediaPlanApiError extends Error {
  constructor(message, statusCode = 0, details = null) {
    super(message);
    this.name = 'MediaPlanApiError';
    this.statusCode = statusCode;
    this.details = details;
  }

  get isNetworkError() {
    return this.statusCode === 0;
  }

  get isClientError() {
    return this.statusCode >= 400 && this.statusCode < 500;
  }

  get isServerError() {
    return this.statusCode >= 500;
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Simulate network latency in mock mode */
const mockDelay = (ms = 500) => new Promise((r) => setTimeout(r, ms));

/** Deep clone to avoid mutation of shared mock objects */
const clone = (obj) => JSON.parse(JSON.stringify(obj));

// ── API Client ──────────────────────────────────────────────────────────────

class MediaPlanApiClient {
  constructor(baseUrl = null) {
    this.baseUrl = baseUrl || API_BASE_URL;
    this.headers = { 'Content-Type': 'application/json' };
  }

  /**
   * Centralized request handler (used when real APIs are wired up)
   */
  async _makeRequest(path, options = {}) {
    const url = `${this.baseUrl}${path}`;

    try {
      const response = await fetch(url, {
        headers: this.headers,
        credentials: 'include',
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new MediaPlanApiError(
          errorData.detail || errorData.message || `HTTP ${response.status}`,
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof MediaPlanApiError) throw error;
      throw new MediaPlanApiError(
        error.message || 'Network error',
        0,
        { originalError: error }
      );
    }
  }

  // ─── Business Discovery ─────────────────────────────────────────────────

  /**
   * Search for a business by name, query, or website URL.
   * Returns a BusinessProfile entity.
   * @param {string} query - Business name, search query, or website URL
   * @returns {Promise<Object>} BusinessProfile object (includes `id`)
   */
  async searchBusiness(query) {
    if (isMockMode()) {
      await mockDelay(600);
      return clone(MOCK_BUSINESS_PROFILE);
    }
    return this._makeRequest('/media-plans/search', {
      method: 'POST',
      body: JSON.stringify({ query }),
    });
  }

  /**
   * Get BusinessProfile by Google Place ID
   * @param {string} placeId
   * @returns {Promise<Object>} BusinessProfile object (includes `id`)
   */
  async getBusinessByPlaceId(placeId) {
    if (isMockMode()) {
      await mockDelay(400);
      return clone(MOCK_BUSINESS_PROFILE);
    }
    return this._makeRequest(`/businesses/${encodeURIComponent(placeId)}`);
  }

  // ─── Plan Generation ────────────────────────────────────────────────────

  /**
   * Generate MediaPlan strategies for a BusinessProfile.
   * Returns enriched plans with both stored fields and computed display data.
   * @param {string} businessProfileId - BusinessProfile ID, place ID, or name
   * @param {number} [budget] - Optional budget constraint
   * @param {Object} [options] - Additional options
   * @param {string} [options.targetingAddress] - Address/zip for ad targeting (geocoded server-side)
   * @returns {Promise<Object[]>} Array of enriched MediaPlan strategies
   */
  async getPlansForBusiness(businessProfileId, budget = null, options = {}) {
    if (isMockMode()) {
      await mockDelay(800);
      let plans = clone(MOCK_PLANS);
      if (budget) {
        // Filter plans within ~50% of the requested budget
        plans = plans.filter((p) => p.budget <= budget * 1.5 && p.budget >= budget * 0.5);
        if (plans.length === 0) plans = clone(MOCK_PLANS); // fallback to all
      }
      // In mock mode, override targeting label if address provided
      if (options.targetingAddress) {
        plans.forEach((p) => {
          p.targeting = { ...p.targeting, centerLabel: options.targetingAddress };
        });
      }
      return plans;
    }
    return this._makeRequest('/media-plans/generate', {
      method: 'POST',
      body: JSON.stringify({
        businessProfileId,
        budget,
        targetingAddress: options.targetingAddress || undefined,
      }),
    });
  }

  /**
   * Get a single MediaPlan by ID (for deep linking / refresh)
   * @param {string} planId - MediaPlan ID
   * @returns {Promise<Object>} Enriched MediaPlan object
   */
  async getPlan(planId) {
    if (isMockMode()) {
      await mockDelay(300);
      const plan = MOCK_PLANS.find((p) => p.id === planId);
      if (!plan) throw new MediaPlanApiError('Plan not found', 404);
      return clone(plan);
    }
    return this._makeRequest(`/media-plans/${encodeURIComponent(planId)}`);
  }

  /**
   * Get differentiators for a BusinessProfile (review-derived suggestions)
   * @param {string} businessProfileId - BusinessProfile ID
   * @returns {Promise<string[]>}
   */
  async getDifferentiators(businessProfileId) {
    if (isMockMode()) {
      await mockDelay(200);
      return [...MOCK_DIFFERENTIATORS];
    }
    return this._makeRequest(`/business-profiles/${encodeURIComponent(businessProfileId)}/differentiators`);
  }

  /**
   * Get available voice options
   * @returns {Promise<Object[]>}
   */
  async getVoiceOptions() {
    if (isMockMode()) {
      return clone(MOCK_VOICE_OPTIONS);
    }
    return this._makeRequest('/config/voice-options');
  }

  /**
   * Get available character options
   * @returns {Promise<Object[]>}
   */
  async getCharacterOptions() {
    if (isMockMode()) {
      return clone(MOCK_CHARACTER_OPTIONS);
    }
    return this._makeRequest('/config/character-options');
  }

  // ─── Customization ──────────────────────────────────────────────────────

  /**
   * Submit creative customization for a MediaPlan.
   * Backend uses this to create/update a Campaign + Creative config.
   * @param {string} planId - MediaPlan ID
   * @param {Object} customization - { creativeBrief, voice, pronunciation, characters, logoFiles, imageFiles }
   * @returns {Promise<Object>} { ok: true }
   */
  async submitCustomization(planId, customization) {
    if (isMockMode()) {
      await mockDelay(400);
      console.log('[Mock] Customization submitted:', { planId, customization });
      return { ok: true };
    }
    // Real implementation would use FormData for file uploads
    return this._makeRequest(`/media-plans/${encodeURIComponent(planId)}/customization`, {
      method: 'POST',
      body: JSON.stringify(customization),
    });
  }

  // ─── Payment ────────────────────────────────────────────────────────────

  /**
   * Process payment and create the full entity chain:
   * User → Org → Advertiser → Lead → Campaign → Creative(s)
   * @param {string} planId - MediaPlan ID
   * @param {Object} paymentData - { email, cardLast4, ... }
   * @returns {Promise<Object>} { success, campaignId, mediaPlanId, businessProfileId }
   */
  async createPayment(planId, paymentData) {
    if (isMockMode()) {
      await mockDelay(2000); // Simulate payment processing
      console.log('[Mock] Payment processed:', { planId, paymentData: { ...paymentData, cardNumber: '****' } });
      return {
        success: true,
        campaignId: 'camp-001',
        mediaPlanId: planId,
        businessProfileId: 'bp-001',
      };
    }
    return this._makeRequest('/payments', {
      method: 'POST',
      body: JSON.stringify({ planId, ...paymentData }),
    });
  }

  // ─── Campaign Generation ────────────────────────────────────────────────

  /**
   * Poll generation status for a campaign's Creatives.
   * In mock mode, simulates progressive step completion based on elapsed time.
   * @param {string} campaignId
   * @returns {Promise<Object>} { currentStep, totalSteps, stepLabel, stepDetail, percentComplete, status }
   */
  async getGenerationStatus(campaignId) {
    if (isMockMode()) {
      await mockDelay(300);

      // Track elapsed calls in a closure-friendly way using sessionStorage
      const key = `mock_gen_start_${campaignId}`;
      let startTime = sessionStorage.getItem(key);
      if (!startTime) {
        startTime = Date.now().toString();
        sessionStorage.setItem(key, startTime);
      }

      const elapsed = Date.now() - parseInt(startTime, 10);
      const steps = MOCK_GENERATION_STEPS;
      const totalDuration = steps.reduce((sum, s) => sum + s.duration, 0);

      if (elapsed >= totalDuration) {
        sessionStorage.removeItem(key);
        return {
          currentStep: steps.length,
          totalSteps: steps.length,
          stepLabel: steps[steps.length - 1].label,
          stepDetail: steps[steps.length - 1].detail,
          percentComplete: 100,
          status: 'completed',
        };
      }

      // Find current step based on elapsed time
      let accumulated = 0;
      for (let i = 0; i < steps.length; i++) {
        accumulated += steps[i].duration;
        if (elapsed < accumulated) {
          const stepProgress = (elapsed - (accumulated - steps[i].duration)) / steps[i].duration;
          const overallProgress = ((i + stepProgress) / steps.length) * 100;
          return {
            currentStep: i + 1,
            totalSteps: steps.length,
            stepLabel: steps[i].label,
            stepDetail: steps[i].detail,
            percentComplete: Math.min(99, Math.round(overallProgress)),
            status: 'generating',
          };
        }
      }

      return { currentStep: steps.length, totalSteps: steps.length, percentComplete: 100, status: 'completed' };
    }
    return this._makeRequest(`/campaigns/${encodeURIComponent(campaignId)}/generation-status`);
  }

  /**
   * Reset the mock generation timer (for retesting)
   */
  resetMockGeneration(campaignId) {
    sessionStorage.removeItem(`mock_gen_start_${campaignId}`);
  }

  // ─── Campaign Creatives (variants) ────────────────────────────────────

  /**
   * Get generated Creatives for a campaign
   * @param {string} campaignId
   * @returns {Promise<Object[]>} Array of Creative objects
   */
  async getCampaignCreatives(campaignId) {
    if (isMockMode()) {
      await mockDelay(500);
      return clone(MOCK_CREATIVES);
    }
    return this._makeRequest(`/campaigns/${encodeURIComponent(campaignId)}/creatives`);
  }

  /** @deprecated Use getCampaignCreatives */
  async getCampaignVariants(campaignId) {
    return this.getCampaignCreatives(campaignId);
  }

  /**
   * Regenerate Creatives with optional feedback
   * @param {string} campaignId
   * @param {string} [feedback] - What to change
   * @returns {Promise<Object[]>} Updated Creative objects
   */
  async regenerateCreatives(campaignId, feedback = '') {
    if (isMockMode()) {
      await mockDelay(3000); // Simulate regeneration
      console.log('[Mock] Regenerating with feedback:', feedback);
      // Return creatives with slightly different descriptions to show the change
      const creatives = clone(MOCK_CREATIVES);
      creatives.forEach((c) => {
        c.description += ' (regenerated)';
      });
      return creatives;
    }
    return this._makeRequest(`/campaigns/${encodeURIComponent(campaignId)}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ feedback }),
    });
  }

  /** @deprecated Use regenerateCreatives */
  async regenerateVariants(campaignId, feedback = '') {
    return this.regenerateCreatives(campaignId, feedback);
  }

  /**
   * Approve campaign and launch
   * @param {string} campaignId
   * @returns {Promise<Object>} Updated Campaign with status 'live'
   */
  async approveCampaign(campaignId) {
    if (isMockMode()) {
      await mockDelay(1500);
      return { ...clone(MOCK_CAMPAIGN), status: 'live', approvedAt: new Date().toISOString() };
    }
    return this._makeRequest(`/campaigns/${encodeURIComponent(campaignId)}/approve`, {
      method: 'POST',
    });
  }

  // ─── Campaign Management ────────────────────────────────────────────────

  /**
   * Get Campaign by ID
   * @param {string} campaignId
   * @returns {Promise<Object>} Campaign object
   */
  async getCampaign(campaignId) {
    if (isMockMode()) {
      await mockDelay(400);
      return clone(MOCK_CAMPAIGN);
    }
    return this._makeRequest(`/campaigns/${encodeURIComponent(campaignId)}`);
  }

  /**
   * Get campaign report with performance data
   * @param {string} campaignId
   * @returns {Promise<Object>} Report with projections, actuals, timeline, benchmarks
   */
  async getCampaignReport(campaignId) {
    if (isMockMode()) {
      await mockDelay(600);
      return clone(MOCK_REPORT);
    }
    return this._makeRequest(`/campaigns/${encodeURIComponent(campaignId)}/report`);
  }

  /**
   * List all campaigns for the current user
   * @returns {Promise<Object[]>} Array of denormalized campaign summary objects
   */
  async listCampaigns() {
    if (isMockMode()) {
      await mockDelay(500);
      return clone(MOCK_DASHBOARD_CAMPAIGNS);
    }
    return this._makeRequest('/campaigns');
  }

  // ─── Notifications ──────────────────────────────────────────────────────

  /**
   * Request email notification when generation is complete
   * @param {string} campaignId
   * @param {string} email
   * @returns {Promise<Object>} { ok: true }
   */
  async requestEmailNotification(campaignId, email) {
    if (isMockMode()) {
      await mockDelay(300);
      console.log('[Mock] Email notification requested:', { campaignId, email });
      return { ok: true };
    }
    return this._makeRequest(`/campaigns/${encodeURIComponent(campaignId)}/notify`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }
}

// ── Singleton export ────────────────────────────────────────────────────────

const mediaPlanApi = new MediaPlanApiClient();
export default mediaPlanApi;

// Re-export isMockMode so screens don't import directly from mockMediaPlanData
export { isMockMode };
